import React, { useState, useEffect } from 'react';
import { useAuth } from '../../lib/auth-context';
import api from '../../lib/api';
import { format } from 'date-fns';
import { 
  Key, 
  Wifi,
  WifiOff,
  Lightbulb,
  Thermometer,
  Tv,
  DoorOpen,
  Building2,
  Plus,
  Loader2,
  RefreshCw,
  Shield,
  ShieldOff,
  Activity,
  Smartphone,
  CheckCircle2,
  XCircle,
  Sun,
  Moon,
  Wind,
  Bell,
  BellOff,
  Settings,
  Zap,
  Power,
  LayoutGrid
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';

export default function SmartRoomPage() {
  const { currentProperty } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Data states
  const [dashboardData, setDashboardData] = useState(null);
  const [devices, setDevices] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [digitalKeys, setDigitalKeys] = useState([]);
  const [scenes, setScenes] = useState([]);
  
  // Dialog states
  const [showDeviceDialog, setShowDeviceDialog] = useState(false);
  const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [showSceneDialog, setShowSceneDialog] = useState(false);
  const [showDeviceTypeDialog, setShowDeviceTypeDialog] = useState(false);
  
  // Form states
  const [deviceForm, setDeviceForm] = useState({
    room_id: '',
    device_type_id: '',
    device_name: '',
    device_identifier: '',
    location_in_room: ''
  });
  
  const [sceneForm, setSceneForm] = useState({
    name: '',
    code: '',
    description: '',
    icon: 'sun',
    settings: {
      lighting: 100,
      temperature: 22,
      curtains: 'open'
    }
  });

  const [deviceTypeForm, setDeviceTypeForm] = useState({
    code: '',
    name: '',
    category: '',
    manufacturer: '',
    model: '',
    protocol: 'WIFI'
  });

  const [keyForm, setKeyForm] = useState({
    guest_id: '',
    room_id: '',
    reservation_id: '',
    key_type: 'MOBILE',
    valid_from: '',
    valid_until: '',
    access_points: ['ROOM_DOOR']
  });

  // Rooms for selection
  const [rooms, setRooms] = useState([]);
  const [guests, setGuests] = useState([]);
  const [reservations, setReservations] = useState([]);

  useEffect(() => {
    if (currentProperty?.id) {
      loadDashboard();
      loadDeviceTypes();
      loadScenes();
      loadRooms();
      loadGuests();
      loadReservations();
    }
  }, [currentProperty]);

  useEffect(() => {
    if (currentProperty?.id && activeTab === 'devices') {
      loadDevices();
    } else if (currentProperty?.id && activeTab === 'keys') {
      loadDigitalKeys();
    }
  }, [currentProperty, activeTab]);

  const loadDashboard = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/smart-room/dashboard?property_id=${currentProperty.id}`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadDevices = async () => {
    try {
      const response = await api.get(`/smart-room/devices?property_id=${currentProperty.id}`);
      setDevices(response.data || []);
    } catch (error) {
      console.error('Error loading devices:', error);
    }
  };

  const loadDeviceTypes = async () => {
    try {
      const response = await api.get(`/smart-room/device-types?property_id=${currentProperty.id}`);
      setDeviceTypes(response.data || []);
    } catch (error) {
      console.error('Error loading device types:', error);
    }
  };

  const loadDigitalKeys = async () => {
    try {
      const response = await api.get(`/smart-room/keys?property_id=${currentProperty.id}`);
      setDigitalKeys(response.data || []);
    } catch (error) {
      console.error('Error loading digital keys:', error);
    }
  };

  const loadScenes = async () => {
    try {
      const response = await api.get(`/smart-room/scenes?property_id=${currentProperty.id}`);
      setScenes(response.data || []);
    } catch (error) {
      console.error('Error loading scenes:', error);
    }
  };

  const loadRooms = async () => {
    try {
      const response = await api.get(`/rooms?property_id=${currentProperty.id}`);
      setRooms(response.data || []);
    } catch (error) {
      console.error('Error loading rooms:', error);
    }
  };

  const loadGuests = async () => {
    try {
      const response = await api.get(`/guests?property_id=${currentProperty.id}`);
      setGuests(response.data || []);
    } catch (error) {
      console.error('Error loading guests:', error);
    }
  };

  const loadReservations = async () => {
    try {
      const response = await api.get(`/reservations?property_id=${currentProperty.id}`);
      setReservations(response.data || []);
    } catch (error) {
      console.error('Error loading reservations:', error);
    }
  };

  const handleCreateDevice = async () => {
    try {
      await api.post(`/smart-room/devices?property_id=${currentProperty.id}`, deviceForm);
      toast.success('Device registered successfully');
      setShowDeviceDialog(false);
      loadDevices();
      loadDashboard();
      resetDeviceForm();
    } catch (error) {
      toast.error('Failed to register device');
    }
  };

  const handleCreateScene = async () => {
    try {
      await api.post(`/smart-room/scenes?property_id=${currentProperty.id}`, sceneForm);
      toast.success('Scene created successfully');
      setShowSceneDialog(false);
      loadScenes();
      resetSceneForm();
    } catch (error) {
      toast.error('Failed to create scene');
    }
  };

  const handleCreateDeviceType = async () => {
    try {
      await api.post(`/smart-room/device-types?property_id=${currentProperty.id}`, deviceTypeForm);
      toast.success('Device type created successfully');
      setShowDeviceTypeDialog(false);
      loadDeviceTypes();
      setDeviceTypeForm({ code: '', name: '', category: '', manufacturer: '', model: '', protocol: 'WIFI' });
    } catch (error) {
      toast.error('Failed to create device type');
    }
  };

  const handleIssueDigitalKey = async () => {
    try {
      await api.post(`/smart-room/keys?property_id=${currentProperty.id}`, keyForm);
      toast.success('Digital key issued successfully');
      setShowKeyDialog(false);
      loadDigitalKeys();
      loadDashboard();
      setKeyForm({ guest_id: '', room_id: '', reservation_id: '', key_type: 'MOBILE', valid_from: '', valid_until: '', access_points: ['ROOM_DOOR'] });
    } catch (error) {
      toast.error('Failed to issue digital key');
    }
  };

  const handleRevokeKey = async (keyId) => {
    try {
      await api.post(`/smart-room/keys/${keyId}/revoke?reason=Manual revocation`);
      toast.success('Key revoked successfully');
      loadDigitalKeys();
      loadDashboard();
    } catch (error) {
      toast.error('Failed to revoke key');
    }
  };

  const resetDeviceForm = () => {
    setDeviceForm({
      room_id: '',
      device_type_id: '',
      device_name: '',
      device_identifier: '',
      location_in_room: ''
    });
  };

  const resetSceneForm = () => {
    setSceneForm({
      name: '',
      code: '',
      description: '',
      icon: 'sun',
      settings: {
        lighting: 100,
        temperature: 22,
        curtains: 'open'
      }
    });
  };

  const getDeviceIcon = (category) => {
    switch (category) {
      case 'LOCK': return DoorOpen;
      case 'THERMOSTAT': return Thermometer;
      case 'LIGHTING': return Lightbulb;
      case 'TV': return Tv;
      case 'CURTAIN': return Sun;
      default: return Settings;
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'ONLINE': case 'ACTIVE': return 'default';
      case 'OFFLINE': case 'EXPIRED': case 'REVOKED': return 'destructive';
      case 'MAINTENANCE': case 'PENDING': return 'secondary';
      default: return 'outline';
    }
  };

  const getKeyTypeLabel = (type) => {
    const labels = {
      MOBILE: 'Mobile App',
      NFC: 'NFC',
      BLUETOOTH: 'Bluetooth',
      QR_CODE: 'QR Code'
    };
    return labels[type] || type;
  };

  if (!currentProperty) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="smartroom-no-property">
        <div className="text-center">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">No Property Selected</h3>
          <p className="text-muted-foreground">Please select a property</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="smartroom-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="smart-room-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Smart Room & IoT</h1>
          <p className="text-muted-foreground">Digital keys, room controls, and IoT devices</p>
        </div>
        <Button onClick={loadDashboard} variant="outline" data-testid="refresh-smartroom-btn">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="tab-smartroom-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="devices" data-testid="tab-devices">Devices</TabsTrigger>
          <TabsTrigger value="keys" data-testid="tab-keys">Digital Keys</TabsTrigger>
          <TabsTrigger value="scenes" data-testid="tab-scenes">Room Scenes</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card data-testid="stat-devices">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">IoT Devices</CardTitle>
                <Wifi className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.devices?.total || 0}</div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-green-500"></span>
                    {dashboardData?.devices?.online || 0} online
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-red-500"></span>
                    {dashboardData?.devices?.offline || 0} offline
                  </span>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-active-keys">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Active Keys</CardTitle>
                <Key className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.keys?.active || 0}</div>
                <p className="text-xs text-muted-foreground">Digital room keys issued</p>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-accesses-today">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Key Accesses Today</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.keys?.accesses_today || 0}</div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="text-green-500">{dashboardData?.keys?.successful || 0} successful</span>
                  <span className="text-red-500">{dashboardData?.keys?.failed || 0} failed</span>
                </div>
              </CardContent>
            </Card>
            
            <Card data-testid="stat-dnd-rooms">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">DND Active</CardTitle>
                <BellOff className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{dashboardData?.rooms?.dnd_active || 0}</div>
                <p className="text-xs text-muted-foreground">Rooms with Do Not Disturb</p>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => { setActiveTab('devices'); setShowDeviceDialog(true); }}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <Wifi className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Register Device</h3>
                    <p className="text-sm text-muted-foreground">Add new IoT device</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => setActiveTab('keys')}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <Key className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Manage Keys</h3>
                    <p className="text-sm text-muted-foreground">View digital keys</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="cursor-pointer hover:border-primary transition-colors" onClick={() => { setActiveTab('scenes'); setShowSceneDialog(true); }}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                    <LayoutGrid className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Create Scene</h3>
                    <p className="text-sm text-muted-foreground">Room presets</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Room Scenes Preview */}
          {scenes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Available Scenes</CardTitle>
                <CardDescription>Pre-configured room settings</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-4">
                  {scenes.map((scene) => (
                    <div 
                      key={scene.id}
                      className="p-4 border rounded-lg hover:border-primary cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        {scene.icon === 'sun' ? <Sun className="h-5 w-5 text-yellow-500" /> :
                         scene.icon === 'moon' ? <Moon className="h-5 w-5 text-blue-500" /> :
                         <Lightbulb className="h-5 w-5 text-amber-500" />}
                        <span className="font-medium">{scene.name}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{scene.description || 'No description'}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Devices Tab */}
        <TabsContent value="devices" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>IoT Devices</CardTitle>
                  <CardDescription>Manage room devices and sensors</CardDescription>
                </div>
                <Button onClick={() => setShowDeviceDialog(true)} data-testid="add-device-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Register Device
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Device</TableHead>
                    <TableHead>Room</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Seen</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {devices.map((device) => {
                    const DeviceIcon = getDeviceIcon(device.device_types?.category);
                    return (
                      <TableRow key={device.id} data-testid={`device-row-${device.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <DeviceIcon className="h-5 w-5 text-muted-foreground" />
                            <div>
                              <div className="font-medium">{device.device_name}</div>
                              <div className="text-sm text-muted-foreground">{device.device_identifier}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{device.rooms?.room_number || '-'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{device.device_types?.category}</Badge>
                        </TableCell>
                        <TableCell>{device.location_in_room || '-'}</TableCell>
                        <TableCell>
                          <Badge variant={getStatusBadgeVariant(device.status)}>
                            {device.status === 'ONLINE' ? (
                              <span className="flex items-center gap-1">
                                <Wifi className="h-3 w-3" /> Online
                              </span>
                            ) : (
                              <span className="flex items-center gap-1">
                                <WifiOff className="h-3 w-3" /> {device.status}
                              </span>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {device.last_seen_at ? format(new Date(device.last_seen_at), 'MMM d, HH:mm') : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm">
                            <Settings className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {devices.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No devices registered
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Digital Keys Tab */}
        <TabsContent value="keys" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Digital Keys</CardTitle>
                  <CardDescription>Mobile room keys issued to guests</CardDescription>
                </div>
                <Button onClick={() => { loadGuests(); loadReservations(); setShowKeyDialog(true); }} data-testid="add-digital-key-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Issue Digital Key
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Guest</TableHead>
                    <TableHead>Room</TableHead>
                    <TableHead>Key Type</TableHead>
                    <TableHead>Valid Period</TableHead>
                    <TableHead>Uses</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {digitalKeys.map((key) => (
                    <TableRow key={key.id} data-testid={`key-row-${key.id}`}>
                      <TableCell>
                        <div className="font-medium">
                          {key.guests?.first_name} {key.guests?.last_name}
                        </div>
                      </TableCell>
                      <TableCell>{key.rooms?.room_number || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          <Smartphone className="h-3 w-3 mr-1" />
                          {getKeyTypeLabel(key.key_type)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div>{format(new Date(key.valid_from), 'MMM d, HH:mm')}</div>
                        <div className="text-muted-foreground">to {format(new Date(key.valid_until), 'MMM d, HH:mm')}</div>
                      </TableCell>
                      <TableCell>{key.use_count || 0}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(key.status)}>
                          {key.status === 'ACTIVE' ? (
                            <span className="flex items-center gap-1">
                              <Shield className="h-3 w-3" /> Active
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <ShieldOff className="h-3 w-3" /> {key.status}
                            </span>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {key.status === 'ACTIVE' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleRevokeKey(key.id)}
                          >
                            <XCircle className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {digitalKeys.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No digital keys issued
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scenes Tab */}
        <TabsContent value="scenes" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Room Scenes</CardTitle>
                  <CardDescription>Pre-configured room settings</CardDescription>
                </div>
                <Button onClick={() => setShowSceneDialog(true)} data-testid="add-scene-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Scene
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                {scenes.map((scene) => (
                  <Card key={scene.id} className="relative" data-testid={`scene-card-${scene.id}`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                            {scene.icon === 'sun' ? <Sun className="h-5 w-5 text-yellow-500" /> :
                             scene.icon === 'moon' ? <Moon className="h-5 w-5 text-blue-500" /> :
                             <Lightbulb className="h-5 w-5 text-amber-500" />}
                          </div>
                          <div>
                            <h3 className="font-semibold">{scene.name}</h3>
                            <p className="text-sm text-muted-foreground">{scene.code}</p>
                          </div>
                        </div>
                        {scene.is_system && <Badge variant="secondary">System</Badge>}
                      </div>
                      <p className="mt-3 text-sm text-muted-foreground">{scene.description || 'No description'}</p>
                      <div className="mt-4 space-y-2">
                        {scene.settings?.lighting !== undefined && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2">
                              <Lightbulb className="h-4 w-4" /> Lighting
                            </span>
                            <span>{scene.settings.lighting}%</span>
                          </div>
                        )}
                        {scene.settings?.temperature !== undefined && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2">
                              <Thermometer className="h-4 w-4" /> Temperature
                            </span>
                            <span>{scene.settings.temperature}°C</span>
                          </div>
                        )}
                        {scene.settings?.curtains !== undefined && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="flex items-center gap-2">
                              <Sun className="h-4 w-4" /> Curtains
                            </span>
                            <span className="capitalize">{scene.settings.curtains}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {scenes.length === 0 && (
                  <div className="col-span-3 text-center py-8 text-muted-foreground">
                    No scenes created
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Register Device Dialog */}
      <Dialog open={showDeviceDialog} onOpenChange={setShowDeviceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Register Device</DialogTitle>
            <DialogDescription>Add a new IoT device to a room</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Room *</Label>
              <Select value={deviceForm.room_id} onValueChange={(v) => setDeviceForm({...deviceForm, room_id: v})}>
                <SelectTrigger data-testid="select-device-room">
                  <SelectValue placeholder="Select room" />
                </SelectTrigger>
                <SelectContent>
                  {rooms.map((room) => (
                    <SelectItem key={room.id} value={room.id}>{room.room_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Device Type *</Label>
              <div className="flex gap-2">
                <Select value={deviceForm.device_type_id} onValueChange={(v) => setDeviceForm({...deviceForm, device_type_id: v})}>
                  <SelectTrigger data-testid="select-device-type" className="flex-1">
                    <SelectValue placeholder={deviceTypes.length === 0 ? "No device types - add one first" : "Select type"} />
                  </SelectTrigger>
                  <SelectContent>
                    {deviceTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>{type.name} ({type.category})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" size="icon" onClick={() => setShowDeviceTypeDialog(true)} data-testid="add-device-type-btn" title="Add Device Type">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Device Name *</Label>
              <Input
                value={deviceForm.device_name}
                onChange={(e) => setDeviceForm({...deviceForm, device_name: e.target.value})}
                placeholder="e.g., Main Light"
                data-testid="input-device-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Device Identifier *</Label>
              <Input
                value={deviceForm.device_identifier}
                onChange={(e) => setDeviceForm({...deviceForm, device_identifier: e.target.value})}
                placeholder="e.g., LOCK-101-001"
                data-testid="input-device-id"
              />
            </div>
            <div className="space-y-2">
              <Label>Location in Room</Label>
              <Input
                value={deviceForm.location_in_room}
                onChange={(e) => setDeviceForm({...deviceForm, location_in_room: e.target.value})}
                placeholder="e.g., Entrance, Bathroom"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeviceDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateDevice} data-testid="submit-device-btn">Register Device</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Scene Dialog */}
      <Dialog open={showSceneDialog} onOpenChange={setShowSceneDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Room Scene</DialogTitle>
            <DialogDescription>Configure a preset room setting</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Scene Name *</Label>
                <Input
                  value={sceneForm.name}
                  onChange={(e) => setSceneForm({...sceneForm, name: e.target.value})}
                  placeholder="e.g., Wake Up"
                  data-testid="input-scene-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  value={sceneForm.code}
                  onChange={(e) => setSceneForm({...sceneForm, code: e.target.value})}
                  placeholder="e.g., WAKE_UP"
                  data-testid="input-scene-code"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={sceneForm.description}
                onChange={(e) => setSceneForm({...sceneForm, description: e.target.value})}
                placeholder="Scene description"
              />
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <Select value={sceneForm.icon} onValueChange={(v) => setSceneForm({...sceneForm, icon: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sun">Sun (Day)</SelectItem>
                  <SelectItem value="moon">Moon (Night)</SelectItem>
                  <SelectItem value="lightbulb">Light</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-4 p-4 bg-muted rounded-lg">
              <h4 className="font-medium">Settings</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Lighting</Label>
                  <span>{sceneForm.settings.lighting}%</span>
                </div>
                <Input
                  type="range"
                  min="0"
                  max="100"
                  value={sceneForm.settings.lighting}
                  onChange={(e) => setSceneForm({
                    ...sceneForm, 
                    settings: {...sceneForm.settings, lighting: parseInt(e.target.value)}
                  })}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Temperature</Label>
                  <span>{sceneForm.settings.temperature}°C</span>
                </div>
                <Input
                  type="range"
                  min="16"
                  max="28"
                  value={sceneForm.settings.temperature}
                  onChange={(e) => setSceneForm({
                    ...sceneForm, 
                    settings: {...sceneForm.settings, temperature: parseInt(e.target.value)}
                  })}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label>Curtains</Label>
                <Select 
                  value={sceneForm.settings.curtains} 
                  onValueChange={(v) => setSceneForm({
                    ...sceneForm, 
                    settings: {...sceneForm.settings, curtains: v}
                  })}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                    <SelectItem value="half">Half</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSceneDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateScene} data-testid="submit-scene-btn">Create Scene</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Device Type Dialog */}
      <Dialog open={showDeviceTypeDialog} onOpenChange={setShowDeviceTypeDialog}>
        <DialogContent className="max-w-md" data-testid="device-type-dialog">
          <DialogHeader>
            <DialogTitle>Add Device Type</DialogTitle>
            <DialogDescription>Register a new IoT device type</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code *</Label>
                <Input
                  value={deviceTypeForm.code}
                  onChange={(e) => setDeviceTypeForm({...deviceTypeForm, code: e.target.value})}
                  placeholder="e.g., SMART_LOCK_01"
                  data-testid="input-dt-code"
                />
              </div>
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={deviceTypeForm.name}
                  onChange={(e) => setDeviceTypeForm({...deviceTypeForm, name: e.target.value})}
                  placeholder="e.g., Smart Door Lock"
                  data-testid="input-dt-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={deviceTypeForm.category} onValueChange={(v) => setDeviceTypeForm({...deviceTypeForm, category: v})}>
                <SelectTrigger data-testid="select-dt-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOCK">Lock</SelectItem>
                  <SelectItem value="THERMOSTAT">Thermostat</SelectItem>
                  <SelectItem value="LIGHTING">Lighting</SelectItem>
                  <SelectItem value="TV">TV / Entertainment</SelectItem>
                  <SelectItem value="CURTAIN">Curtain / Blinds</SelectItem>
                  <SelectItem value="SENSOR">Sensor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Manufacturer</Label>
                <Input
                  value={deviceTypeForm.manufacturer}
                  onChange={(e) => setDeviceTypeForm({...deviceTypeForm, manufacturer: e.target.value})}
                  placeholder="e.g., Assa Abloy"
                />
              </div>
              <div className="space-y-2">
                <Label>Model</Label>
                <Input
                  value={deviceTypeForm.model}
                  onChange={(e) => setDeviceTypeForm({...deviceTypeForm, model: e.target.value})}
                  placeholder="e.g., VingCard Essence"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Protocol</Label>
              <Select value={deviceTypeForm.protocol} onValueChange={(v) => setDeviceTypeForm({...deviceTypeForm, protocol: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="WIFI">WiFi</SelectItem>
                  <SelectItem value="ZIGBEE">Zigbee</SelectItem>
                  <SelectItem value="ZWAVE">Z-Wave</SelectItem>
                  <SelectItem value="BLE">Bluetooth LE</SelectItem>
                  <SelectItem value="MQTT">MQTT</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeviceTypeDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateDeviceType} disabled={!deviceTypeForm.code || !deviceTypeForm.name || !deviceTypeForm.category} data-testid="submit-device-type-btn">Create Device Type</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Issue Digital Key Dialog */}
      <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
        <DialogContent className="max-w-md" data-testid="digital-key-dialog">
          <DialogHeader>
            <DialogTitle>Issue Digital Key</DialogTitle>
            <DialogDescription>Issue a mobile room key to a guest</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Guest *</Label>
              <Select value={keyForm.guest_id} onValueChange={(v) => setKeyForm({...keyForm, guest_id: v})}>
                <SelectTrigger data-testid="select-key-guest">
                  <SelectValue placeholder="Select guest" />
                </SelectTrigger>
                <SelectContent>
                  {guests.map((guest) => (
                    <SelectItem key={guest.id} value={guest.id}>{guest.first_name} {guest.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Room *</Label>
              <Select value={keyForm.room_id} onValueChange={(v) => setKeyForm({...keyForm, room_id: v})}>
                <SelectTrigger data-testid="select-key-room">
                  <SelectValue placeholder="Select room" />
                </SelectTrigger>
                <SelectContent>
                  {rooms.map((room) => (
                    <SelectItem key={room.id} value={room.id}>Room {room.room_number}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reservation</Label>
              <Select value={keyForm.reservation_id} onValueChange={(v) => setKeyForm({...keyForm, reservation_id: v})}>
                <SelectTrigger data-testid="select-key-reservation">
                  <SelectValue placeholder="Select reservation (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {reservations.map((res) => (
                    <SelectItem key={res.id} value={res.id}>{res.confirmation_number} - {res.guest_name || 'Guest'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Key Type</Label>
              <Select value={keyForm.key_type} onValueChange={(v) => setKeyForm({...keyForm, key_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MOBILE">Mobile Key</SelectItem>
                  <SelectItem value="RFID">RFID Card</SelectItem>
                  <SelectItem value="NFC">NFC</SelectItem>
                  <SelectItem value="PIN">PIN Code</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Valid From *</Label>
                <Input
                  type="datetime-local"
                  value={keyForm.valid_from}
                  onChange={(e) => setKeyForm({...keyForm, valid_from: e.target.value})}
                  data-testid="input-key-valid-from"
                />
              </div>
              <div className="space-y-2">
                <Label>Valid Until *</Label>
                <Input
                  type="datetime-local"
                  value={keyForm.valid_until}
                  onChange={(e) => setKeyForm({...keyForm, valid_until: e.target.value})}
                  data-testid="input-key-valid-until"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowKeyDialog(false)}>Cancel</Button>
            <Button onClick={handleIssueDigitalKey} disabled={!keyForm.guest_id || !keyForm.room_id || !keyForm.valid_from || !keyForm.valid_until} data-testid="submit-digital-key-btn">Issue Key</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
